import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageTitle } from "../../components/Layout.tsx";
import StyledButton from "../../components/Button.tsx";
import { fetchAllIngestionJobs } from "../../services/categories.service.ts";
import type { IngestionJob } from "../../services/categories.service.ts";
import { VscodeFileTypePdf2Icon } from "../../icons/vscodeIconsBundled.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const JobList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const JobRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  background: #f5f5f5;
  border-radius: 10px;
  transition: background 0.3s ease;

  .dark-mode & {
    background: #3a3a4e;
  }
`;

const PdfIconWrapper = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.1rem;

  svg {
    display: block;
  }
`;

const JobBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const JobTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #001f52;
  word-break: break-word;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-top: 0.15rem;
`;

const StatusBadge = styled.span<{ $status: string }>`
  flex-shrink: 0;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) => {
    switch (p.$status) {
      case "queued":
        return "#fff3e0";
      case "processing":
        return "#e3f2fd";
      case "completed":
        return "#e8f5e9";
      case "failed":
        return "#ffebee";
      default:
        return "#f0f0f0";
    }
  }};
  color: ${(p) => {
    switch (p.$status) {
      case "queued":
        return "#f57c00";
      case "processing":
        return "#1976d2";
      case "completed":
        return "#2e7d32";
      case "failed":
        return "#c62828";
      default:
        return "#666";
    }
  }};
  transition: background 0.3s ease, color 0.3s ease;

  .dark-mode & {
    background: ${(p) => {
      switch (p.$status) {
        case "queued":
          return "#3a2f1f";
        case "processing":
          return "#1a2a3a";
        case "completed":
          return "#1a3a1f";
        case "failed":
          return "#3a1f1f";
        default:
          return "#2a2a3e";
      }
    }};
    color: ${(p) => {
      switch (p.$status) {
        case "queued":
          return "#ffb74d";
        case "processing":
          return "#64b5f6";
        case "completed":
          return "#81c784";
        case "failed":
          return "#ff6b6b";
        default:
          return "#bbb";
      }
    }};
  }
`;

const ProgressTrack = styled.div`
  flex: 1;
  min-width: 120px;
  height: 8px;
  background: #e0e0e0;
  border-radius: 999px;
  overflow: hidden;
  transition: background 0.3s ease;

  .dark-mode & {
    background: #4a4a5e;
  }
`;

const ProgressFill = styled.div<{ $percentage: number }>`
  height: 100%;
  width: ${(p) => p.$percentage}%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.span`
  flex-shrink: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  min-width: 2.5rem;
  text-align: right;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const StatusMessage = styled.span`
  font-size: 0.85rem;
  color: #555;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #c0c0c0;
  }
`;

const ErrorMessageInline = styled.span`
  font-size: 0.85rem;
  color: #c62828;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #ff6b6b;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #666;
  font-size: 0.95rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 1.5rem;
  color: #666;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #b0b0b0;
  }
`;

const ErrorBanner = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  border-left: 4px solid #c62828;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  .dark-mode & {
    background-color: #3a1f1f;
    color: #ff6b6b;
    border-left-color: #ff6b6b;
  }
`;

const STATUS_ALIASES: Record<string, "queued" | "processing" | "completed" | "failed"> = {
  queued: "queued",
  "i kø": "queued",
  processing: "processing",
  behandler: "processing",
  klargør: "processing",
  "klargør...": "processing",
  klargøring: "processing",
  completed: "completed",
  færdig: "completed",
  failed: "failed",
  fejl: "failed",
  fejlet: "failed",
};

const STATUS_LABELS: Record<"queued" | "processing" | "completed" | "failed", string> = {
  queued: "I KØ",
  processing: "KLARGØR",
  completed: "FÆRDIG",
  failed: "FEJL",
};

const TOTAL_PIPELINE_STEPS = 8;

function normalizeStatus(rawStatus?: string | null): "queued" | "processing" | "completed" | "failed" | null {
  if (!rawStatus) return null;
  return STATUS_ALIASES[rawStatus.toLowerCase().trim()] ?? null;
}

function computeProgressPercentage(job: IngestionJob): number {
  const totalSteps = job.total_steps ?? TOTAL_PIPELINE_STEPS;

  let pct = 0;
  if (typeof job.step === "number" && totalSteps > 0) {
    pct = (job.step / totalSteps) * 100;
  } else if (job.steps_completed && job.steps_completed.length > 0) {
    pct = (job.steps_completed.length / totalSteps) * 100;
  }

  return Math.min(Math.max(pct, 0), 100);
}

function isActiveStatus(status: ReturnType<typeof normalizeStatus>): boolean {
  return status === "queued" || status === "processing";
}

function jobSortKey(job: IngestionJob): number {
  const ts = job.created_at ? Date.parse(job.created_at) : NaN;
  return Number.isNaN(ts) ? 0 : ts;
}

export default function DocumentStatusPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<IngestionJob[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const refresh = useCallback(async () => {
    const fresh = await fetchAllIngestionJobs();
    if (!isMountedRef.current) return fresh;
    setJobs(Array.isArray(fresh) ? fresh : []);
    return fresh;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    let timeoutId: number | null = null;

    const scheduleNext = (delayMs: number) => {
      if (!isMountedRef.current) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = window.setTimeout(tick, delayMs);
    };

    async function tick() {
      if (!isMountedRef.current) return;
      let nextDelayMs = 30000;
      try {
        const fresh = await refresh();
        if (!isMountedRef.current) return;
        const hasActive = (fresh ?? []).some((j) => isActiveStatus(normalizeStatus(j.status)));
        nextDelayMs = hasActive ? 5000 : 30000;
        setError(null);
      } catch (err) {
        console.error("Failed to fetch ingestion jobs:", err);
        if (isMountedRef.current) {
          try {
            setError(parseApiError(err));
          } catch {
            setError("Der opstod en fejl ved hentning af dokumentstatus.");
          }
        }
      } finally {
        if (isMountedRef.current) {
          setInitialLoading(false);
          scheduleNext(nextDelayMs);
        }
      }
    }

    tick();

    return () => {
      isMountedRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [refresh]);

  const sortedJobs = useMemo(() => {
    return [...jobs].sort((a, b) => jobSortKey(b) - jobSortKey(a));
  }, [jobs]);

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <PageTitle noMarginBottom>Dokumentstatus</PageTitle>
          <Subtitle>Status på klargøring af dokumenter</Subtitle>
        </div>
        <HeaderActions>
          <StyledButton type="button" onClick={() => navigate("/categories")}>
            ← Tilbage til kategorier
          </StyledButton>
        </HeaderActions>
      </PageHeader>

      <Card>
        {error && <ErrorBanner>{error}</ErrorBanner>}

        {initialLoading ? (
          <LoadingMessage>Indlæser dokumentstatus...</LoadingMessage>
        ) : sortedJobs.length === 0 ? (
          <EmptyState>Ingen igangværende eller afsluttede klargøringer.</EmptyState>
        ) : (
          <JobList>
            {sortedJobs.map((job) => {
              const status = normalizeStatus(job.status);
              const filename = job.filename || "Ukendt fil";
              const badgeStatus = status ?? "default";
              const badgeLabel = status ? STATUS_LABELS[status] : (job.status || "UKENDT").toUpperCase();
              const percentage = computeProgressPercentage(job);

              return (
                <JobRow key={job.job_id}>
                  <PdfIconWrapper aria-hidden>
                    <VscodeFileTypePdf2Icon size={36} />
                  </PdfIconWrapper>
                  <JobBody>
                    <JobTitle>{filename}</JobTitle>
                    <StatusRow>
                      <StatusBadge $status={badgeStatus}>{badgeLabel}</StatusBadge>

                      {status === "processing" && (
                        <>
                          <ProgressTrack
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(percentage)}
                          >
                            <ProgressFill $percentage={percentage} />
                          </ProgressTrack>
                          <ProgressLabel>{Math.round(percentage)}%</ProgressLabel>
                        </>
                      )}

                      {status === "queued" && (
                        <StatusMessage>Dokumentet venter på at blive klargjort.</StatusMessage>
                      )}

                      {status === "completed" && (
                        <StatusMessage>Dokumentet er klar til brug i chatbotten</StatusMessage>
                      )}

                      {status === "failed" && (
                        <ErrorMessageInline>
                          {job.error || "Der opstod en fejl under klargøring af dokumentet"}
                        </ErrorMessageInline>
                      )}
                    </StatusRow>
                  </JobBody>
                </JobRow>
              );
            })}
          </JobList>
        )}
      </Card>
    </PageContainer>
  );
}
